import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, Edit01, XCircle, CheckCircle, X, Send01, AlertCircle } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { shipperQuoteService, generateMockQuoteResponse } from '@/data/mock-quotes';
import { addressBookService } from '@/data/mock-address-book';
import type { ShipperQuoteRequest, ShipperQuoteStatus, QuoteResponse } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { QuoteResponsesList } from '@/components/application/quotes/quote-responses-list';
import { QuoteResponseDetailPanel } from '@/components/application/quotes/quote-response-detail-panel';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { ShipperQuoteForm } from '@/components/application/quotes/shipper-quote-form';
import { Tabs } from '@/components/application/tabs/tabs';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';

export const ShipperQuoteDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [quoteRequest, setQuoteRequest] = useState<ShipperQuoteRequest | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'details' | 'responses'>(() => {
        const tabParam = searchParams.get('tab');
        return (tabParam === 'responses' ? 'responses' : 'details') as 'details' | 'responses';
    });
    const [selectedResponse, setSelectedResponse] = useState<QuoteResponse | null>(null);
    const [isCheckingForQuotes, setIsCheckingForQuotes] = useState(false);
    const [hasNewQuotes, setHasNewQuotes] = useState(false);
    const [newQuoteId, setNewQuoteId] = useState<string | null>(null);
    const [confirmAcceptId, setConfirmAcceptId] = useState<string | null>(null);
    const [modificationQuoteId, setModificationQuoteId] = useState<string | null>(null);
    const [isCompleteRequestModalOpen, setIsCompleteRequestModalOpen] = useState(false);
    const [isCreatingContainer, setIsCreatingContainer] = useState(false);
    const [reminderRecipientId, setReminderRecipientId] = useState<string | null>(null);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const shipperId = localStorage.getItem('accountId') || '1';

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            if (id) {
                const request = shipperQuoteService.getById(id);
                setQuoteRequest(request || null);
                
                // Se c'è un quoteId nei query params, naviga a quella quote
                const quoteId = searchParams.get('quoteId');
                if (quoteId && id) {
                    // Navigate to the quote detail page instead of opening panel
                    navigate(`/shipper/quotes/${id}/response/${quoteId}`);
                    // Clean up the query param
                    setSearchParams({ tab: 'responses' });
                }
            }

            setIsLoading(false);
        };
        loadData();
    }, [id, searchParams, setSearchParams]);

    // Simulazione ricezione quote con timer random
    useEffect(() => {
        if (isLoading || !quoteRequest || quoteRequest.status === 'completed' || quoteRequest.status === 'cancelled') {
            return;
        }

        const startQuoteCheck = () => {
            // Timer random tra 5 e 15 secondi
            const randomDelay = 5000 + Math.random() * 10000; // 5-15 secondi in millisecondi
            
            timerRef.current = setTimeout(() => {
                setIsCheckingForQuotes(true);
                
                // Simula controllo quote (1-2 secondi)
                checkTimeoutRef.current = setTimeout(() => {
                    // Random booleano per determinare se ci sono nuove quote
                    const hasNewQuote = Math.random() > 0.5;
                    
                    if (hasNewQuote && id) {
                        // Ricarica la richiesta aggiornata
                        const currentRequest = shipperQuoteService.getById(id);
                        if (!currentRequest) {
                            setIsCheckingForQuotes(false);
                            startQuoteCheck();
                            return;
                        }
                        
                        // Seleziona un recipient random che non ha ancora risposto
                        const respondedRecipientIds = new Set(
                            currentRequest.receivedQuotes.map(qr => {
                                // Trova il recipient ID dalla quote response
                                const recipient = addressBookService.getAll().find(
                                    entry => entry.email === qr.from && entry.role === 'Freight Forwarder'
                                );
                                return recipient?.id;
                            }).filter(Boolean)
                        );
                        
                        const availableRecipients = currentRequest.recipients.filter(
                            rid => !respondedRecipientIds.has(rid)
                        );
                        
                        if (availableRecipients.length > 0) {
                            const randomRecipientId = availableRecipients[Math.floor(Math.random() * availableRecipients.length)];
                            
                            try {
                                const newQuote = generateMockQuoteResponse(id, randomRecipientId);
                                shipperQuoteService.addQuoteResponse(id, newQuote);
                                
                                // Ricarica i dati
                                const updated = shipperQuoteService.getById(id);
                                if (updated) {
                                    setQuoteRequest(updated);
                                    setNewQuoteId(newQuote.id);
                                    setHasNewQuotes(true);
                                }
                            } catch (error) {
                                console.error('Error generating mock quote:', error);
                            }
                        }
                    }
                    
                    setIsCheckingForQuotes(false);
                    
                    // Riavvia il timer per il prossimo controllo
                    startQuoteCheck();
                }, 1000 + Math.random() * 1000); // 1-2 secondi per il controllo
            }, randomDelay);
        };

        startQuoteCheck();

        // Listener per aggiornamenti booking number
        const handleBookingNumberShared = (event: CustomEvent) => {
            if (event.detail.quoteRequestId === id && id) {
                // Ricarica i dati quando viene condiviso un booking number
                const updated = shipperQuoteService.getById(id);
                if (updated) {
                    setQuoteRequest(updated);
                }
            }
        };

        window.addEventListener('bookingNumberShared', handleBookingNumberShared as EventListener);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
                checkTimeoutRef.current = null;
            }
            window.removeEventListener('bookingNumberShared', handleBookingNumberShared as EventListener);
        };
    }, [quoteRequest?.id, quoteRequest?.status, id, isLoading]);

    const getStatusColor = (status: ShipperQuoteStatus) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'responded': return 'success';
            case 'completed': return 'success';
            case 'cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: ShipperQuoteStatus) => {
        const statusMap: Record<ShipperQuoteStatus, string> = {
            'pending': 'shipper.status.pending',
            'responded': 'shipper.status.responded',
            'completed': 'shipper.status.completed',
            'cancelled': 'shipper.status.cancelled',
        };
        return t(statusMap[status]) || status;
    };

    const handleSelectQuote = (quoteResponseId: string) => {
        if (id && quoteRequest) {
            const success = shipperQuoteService.selectQuote(id, quoteResponseId);
            if (success) {
                const updated = shipperQuoteService.getById(id);
                if (updated) {
                    setQuoteRequest(updated);
                }
            }
        }
    };

    const handleComplete = () => {
        // Già gestito in handleSelectQuote
    };

    const handleUpdate = (data: any) => {
        if (id) {
            const updated = shipperQuoteService.update(id, data);
            if (updated) {
                setQuoteRequest(updated);
            }
        }
        setShowEditModal(false);
    };

    const handleCancel = () => {
        if (id) {
            const cancelled = shipperQuoteService.cancel(id);
            if (cancelled) {
                const updated = shipperQuoteService.getById(id);
                if (updated) {
                    setQuoteRequest(updated);
                }
            }
        }
        setConfirmCancelId(null);
    };

    const handleViewResponse = (quote: QuoteResponse) => {
        navigate(`/shipper/quotes/${id}/response/${quote.id}`);
    };

    const handleClosePanel = () => {
        setSelectedResponse(null);
    };

    const handleMarkAsRead = (quoteId: string) => {
        if (id && quoteRequest) {
            const updated = shipperQuoteService.markQuoteAsRead(id, quoteId);
            if (updated) {
                setQuoteRequest(updated);
            }
        }
    };

    const handleAcceptQuote = (quoteId: string) => {
        setConfirmAcceptId(quoteId);
    };

    const handleRejectQuote = (quoteId: string) => {
        if (id && quoteRequest) {
            const updated = shipperQuoteService.updateQuoteResponseStatus(id, quoteId, 'rejected');
            if (updated) {
                setQuoteRequest(updated);
            }
        }
    };

    const handleRequestModification = (quoteId: string, body?: string, subject?: string) => {
        // Se body è fornito, significa che l'editor è già stato compilato e possiamo salvare
        if (body !== undefined && body.trim()) {
            if (id && quoteRequest) {
                const updated = shipperQuoteService.saveModificationRequestBody(id, quoteId, body, subject);
                if (updated) {
                    setQuoteRequest(updated);
                }
            }
            setModificationQuoteId(null);
        } else {
            // Se body non è fornito, naviga alla pagina di dettaglio con l'editor aperto
            if (id) {
                navigate(`/shipper/quotes/${id}/response/${quoteId}?modification=true`);
            } else {
                // Fallback: apriamo il pannello di dettaglio con l'editor aperto (solo se non abbiamo id)
                const quote = quoteRequest?.receivedQuotes.find(q => q.id === quoteId);
                if (quote) {
                    setSelectedResponse(quote);
                    setModificationQuoteId(quoteId);
                }
            }
        }
    };

    const handleViewNewQuote = () => {
        if (newQuoteId && id) {
            // Navigate to the new quote detail page
            navigate(`/shipper/quotes/${id}/response/${newQuoteId}`);
            setHasNewQuotes(false);
            setNewQuoteId(null);
        }
    };

    // Calcola il numero di nuove risposte (non lette)
    const unreadCount = useMemo(() => {
        if (!quoteRequest) return 0;
        return quoteRequest.receivedQuotes.filter(q => !q.isRead).length;
    }, [quoteRequest]);

    const hasAcceptedQuotes = useMemo(() => {
        if (!quoteRequest) return false;
        return quoteRequest.receivedQuotes.some(qr => qr.status === 'accepted');
    }, [quoteRequest]);

    const hasBookingNumber = useMemo(() => {
        if (!quoteRequest) return false;
        return quoteRequest.receivedQuotes.some(qr => qr.status === 'accepted' && qr.bookingNumber);
    }, [quoteRequest]);

    const canCompleteRequest = useMemo(() => {
        return hasAcceptedQuotes && hasBookingNumber;
    }, [hasAcceptedQuotes, hasBookingNumber]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('shipper.detail.loading') || 'Caricamento...'}</p>
            </div>
        );
    }

    if (!quoteRequest) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('shipper.detail.notFound') || 'Richiesta non trovata'}</p>
            </div>
        );
    }

    const recipients = quoteRequest.recipients.map(recipientId => {
        const entry = addressBookService.getById(recipientId);
        return entry;
    }).filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    const handleSendReminder = async (recipientId: string) => {
        if (!quoteRequest || !id) return;
        
        setIsSendingReminder(true);
        const recipient = addressBookService.getById(recipientId);
        
        try {
            // Simula invio sollecito (breve delay)
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Qui potresti aggiungere logica per tracciare i solleciti inviati
            console.log(`[ShipperQuoteService] Reminder sent to ${recipient?.firstName} ${recipient?.lastName} (${recipient?.email})`);
            
            // Chiudi il modale
            setReminderRecipientId(null);
        } catch (error) {
            console.error('Error sending reminder:', error);
        } finally {
            setIsSendingReminder(false);
        }
    };

    const tabItems = [
        {
            id: 'details',
            label: t('shipper.detail.tabs.details') || 'Dettaglio Richiesta',
        },
        {
            id: 'responses',
            label: t('shipper.detail.tabs.responses') || 'Quote Ricevute',
            badge: unreadCount > 0 ? unreadCount : undefined,
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate('/shipper/quotes')}
                        color="secondary"
                        size="md"
                        iconLeading={ArrowLeft}
                    >
                        {t('shipper.detail.back') || 'Indietro'}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{quoteRequest.subject}</h1>
                        <div className="flex items-center gap-2 mt-1">
                            <Badge color={getStatusColor(quoteRequest.status)} size="sm">
                                {translateStatus(quoteRequest.status)}
                            </Badge>
                            <span className="text-sm text-tertiary">
                                {t('shipper.detail.sentAt') || 'Inviata il'}: {quoteRequest.sentAt.toLocaleDateString('it-IT', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </span>
                        </div>
                    </div>
                </div>
                {quoteRequest.status !== 'completed' && quoteRequest.status !== 'cancelled' && (
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => setShowEditModal(true)}
                            color="secondary"
                            size="md"
                            iconLeading={Edit01}
                        >
                            {t('shipper.detail.edit') || 'Modifica'}
                        </Button>
                        <Button
                            onClick={() => setConfirmCancelId(quoteRequest.id)}
                            color="primary-destructive"
                            size="md"
                            iconLeading={XCircle}
                        >
                            {t('shipper.detail.cancel') || 'Annulla'}
                        </Button>
                    </div>
                )}
            </div>
            {/* Complete Request CTA */}
            {quoteRequest.status !== 'completed' && quoteRequest.status !== 'cancelled' && hasAcceptedQuotes && (
                <div className={`rounded-xl p-4 shadow-xs ring-1 ${
                    canCompleteRequest 
                        ? 'bg-success-50/10 border border-success-500/20' 
                        : 'bg-warning-50/10 border border-warning-500/20'
                }`}>
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            {canCompleteRequest ? (
                                <CheckCircle className="h-5 w-5 text-success-600" />
                            ) : (
                                <AlertCircle className="h-5 w-5 text-warning-600" />
                            )}
                            <div>
                                <p className="text-sm font-semibold text-primary">
                                    {canCompleteRequest 
                                        ? (t('shipper.detail.readyToComplete') || 'Puoi completare questa richiesta')
                                        : (t('shipper.detail.waitingForBookingNumber') || 'In attesa del booking number')}
                                </p>
                                <p className="text-xs text-tertiary mt-0.5">
                                    {canCompleteRequest 
                                        ? (t('shipper.detail.readyToCompleteDescription') || 'Ci sono una o più quote accettate. Puoi segnare la richiesta come completata e simulare la creazione di container basati su queste quote.')
                                        : (t('shipper.detail.waitingForBookingNumberDescription') || 'Devi attendere che almeno un Freight Forwarder condivida il booking number prima di poter completare la richiesta.')}
                                </p>
                            </div>
                        </div>
                        {canCompleteRequest && (
                            <Button
                                onClick={() => setIsCompleteRequestModalOpen(true)}
                                color="primary"
                                size="sm"
                                iconLeading={CheckCircle}
                            >
                                {t('shipper.detail.completeRequest') || 'Completa richiesta'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Loader per controllo nuove quote */}
            {isCheckingForQuotes && (
                <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <LoadingIndicator type="line-spinner" size="sm" />
                        <span className="text-sm text-tertiary">
                            {t('shipper.detail.checkingQuotes') || 'Controllo nuove quote...'}
                        </span>
                    </div>
                </div>
            )}

            {/* CTA per nuove quote disponibili */}
            {hasNewQuotes && !isCheckingForQuotes && (
                <div className="rounded-xl bg-success-50/10 border border-success-500/20 p-4 shadow-xs ring-1 ring-success-500/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5 text-success-600" />
                            <div>
                                <p className="text-sm font-semibold text-primary">
                                    {t('shipper.detail.newQuotesAvailable') || 'Nuove quote disponibili!'}
                                </p>
                                <p className="text-xs text-tertiary mt-0.5">
                                    {t('shipper.detail.newQuoteReceived') || 'Hai ricevuto una nuova risposta di preventivo'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={handleViewNewQuote}
                                color="primary"
                                size="sm"
                            >
                                {t('shipper.detail.viewNewQuote') || 'Visualizza nuova quote'}
                            </Button>
                            <button
                                onClick={() => {
                                    setHasNewQuotes(false);
                                    setNewQuoteId(null);
                                }}
                                className="text-tertiary hover:text-primary"
                                aria-label={t('common:close') || 'Close'}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => {
                setActiveTab(key as 'details' | 'responses');
                setSearchParams({ tab: key as string });
            }}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id} badge={item.badge}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab: Dettaglio Richiesta */}
                <Tabs.Panel id="details" className="space-y-6">
                    {/* Request Details */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <h2 className="text-lg font-semibold text-primary mb-4">
                            {t('shipper.detail.requestDetails') || 'Dettagli Richiesta'}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm font-medium text-tertiary mb-1">{t('shipper.detail.subject') || 'Oggetto'}</p>
                                <p className="text-primary">{quoteRequest.subject}</p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-tertiary mb-1">{t('shipper.detail.body') || 'Corpo Email'}</p>
                                <div className="rounded-lg bg-secondary p-4 text-sm text-primary whitespace-pre-wrap">
                                    {quoteRequest.body}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recipients with Response Status */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <h2 className="text-lg font-semibold text-primary mb-4">
                            {t('shipper.detail.recipientsTitle') || 'Destinatari'} ({recipients.length})
                        </h2>
                        <div className="space-y-3">
                            {recipients.map((entry) => {
                                // Verifica se questo recipient ha risposto
                                const hasResponded = quoteRequest.receivedQuotes.some(qr => {
                                    // Trova la quote response che corrisponde a questo recipient
                                    const recipientEmail = entry.email;
                                    return qr.from === recipientEmail;
                                });
                                
                                const response = quoteRequest.receivedQuotes.find(qr => qr.from === entry.email);
                                const responseStatus = response?.status || 'pending';
                                
                                return (
                                    <div 
                                        key={entry.id} 
                                        className="flex items-center justify-between p-4 rounded-lg bg-secondary border border-secondary"
                                    >
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-semibold text-primary">
                                                        {entry.firstName} {entry.lastName}
                                                    </p>
                                                    <Badge 
                                                        color={hasResponded ? (responseStatus === 'accepted' ? 'success' : responseStatus === 'rejected' ? 'error' : 'warning') : 'gray'} 
                                                        size="sm"
                                                    >
                                                        {hasResponded 
                                                            ? (responseStatus === 'accepted' 
                                                                ? (t('shipper.detail.recipients.respondedAccepted') || 'Accettato')
                                                                : responseStatus === 'rejected'
                                                                ? (t('shipper.detail.recipients.respondedRejected') || 'Rifiutato')
                                                                : (t('shipper.detail.recipients.respondedModified') || 'Modifica richiesta'))
                                                            : (t('shipper.detail.recipients.pending') || 'In attesa')}
                                                    </Badge>
                                                </div>
                                                <p className="text-xs text-tertiary mt-1">{entry.email}</p>
                                                {hasResponded && response && (
                                                    <p className="text-xs text-tertiary mt-1">
                                                        {t('shipper.detail.recipients.respondedAt') || 'Risposto il'}: {response.receivedAt.toLocaleDateString('it-IT', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {!hasResponded && (
                                            <Button
                                                onClick={() => setReminderRecipientId(entry.id)}
                                                color="secondary"
                                                size="sm"
                                                iconLeading={Send01}
                                            >
                                                {t('shipper.detail.recipients.sendReminder') || 'Invia sollecito'}
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Extracted Data */}
                    {quoteRequest.extractedData && (
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t('shipper.detail.extractedData') || 'Dati della Richiesta'}
                            </h2>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {quoteRequest.extractedData.product && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.product')}</p>
                                        <p className="text-primary">{quoteRequest.extractedData.product}</p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.origin && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.origin')}</p>
                                        <p className="text-primary">{quoteRequest.extractedData.origin}</p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.destination && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.destination')}</p>
                                        <p className="text-primary">{quoteRequest.extractedData.destination}</p>
                                    </div>
                                )}
                                {(quoteRequest.extractedData.quantity || quoteRequest.extractedData.unit) && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.quantity')}</p>
                                        <p className="text-primary">
                                            {quoteRequest.extractedData.quantity} {quoteRequest.extractedData.unit}
                                        </p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.pickupDate && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.pickupDate')}</p>
                                        <p className="text-primary">
                                            {quoteRequest.extractedData.pickupDate.toLocaleDateString('it-IT')}
                                        </p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.deliveryDate && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.deliveryDate')}</p>
                                        <p className="text-primary">
                                            {quoteRequest.extractedData.deliveryDate.toLocaleDateString('it-IT')}
                                        </p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.temperatureRequirements && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.temperatureRequirements')}</p>
                                        <p className="text-primary">{quoteRequest.extractedData.temperatureRequirements}</p>
                                    </div>
                                )}
                                {quoteRequest.extractedData.specialRequirements && quoteRequest.extractedData.specialRequirements.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-tertiary mb-1">{t('extracted.specialRequirements')}</p>
                                        <p className="text-primary">{quoteRequest.extractedData.specialRequirements.join(', ')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Tabs.Panel>

                {/* Tab: Quote Ricevute */}
                <Tabs.Panel id="responses">
                    <div className={`flex flex-col lg:flex-row gap-0 ${selectedResponse ? 'lg:gap-0' : ''}`}>
                        {/* Lista Quote Ricevute */}
                        <div className={`${selectedResponse ? 'lg:w-1/2' : 'w-full'} ${selectedResponse ? 'lg:border-r lg:border-secondary lg:pr-6' : ''}`}>
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h2 className="text-lg font-semibold text-primary mb-4">
                                    {t('shipper.detail.receivedQuotes') || 'Preventivi Ricevuti'} ({quoteRequest.receivedQuotes.length})
                                </h2>
                                <QuoteResponsesList
                                    quoteRequest={quoteRequest}
                                    onSelectQuote={handleSelectQuote}
                                    onComplete={handleComplete}
                                    onViewDetails={handleViewResponse}
                                    onAccept={handleAcceptQuote}
                                    onReject={handleRejectQuote}
                                    onRequestModification={handleRequestModification}
                                />
                            </div>
                        </div>

                        {/* Pannello Dettaglio Quote */}
                        {selectedResponse && (
                            <div className="flex-shrink-0 w-full lg:w-1/2 border-t lg:border-t-0 lg:border-l border-secondary mt-6 lg:mt-0 lg:pl-6">
                                <div className="h-full" style={{ minHeight: 'calc(100vh - 200px)' }}>
                                    <QuoteResponseDetailPanel
                                        quote={selectedResponse}
                                        onClose={handleClosePanel}
                                        onMarkAsRead={handleMarkAsRead}
                                        onAccept={handleAcceptQuote}
                                        onReject={handleRejectQuote}
                                        onRequestModification={handleRequestModification}
                                        quoteRequestStatus={quoteRequest.status}
                                        initialModificationEditorOpen={modificationQuoteId === selectedResponse.id}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Edit Modal */}
            {showEditModal && quoteRequest && (
                <ModalOverlay isOpen={showEditModal} onOpenChange={setShowEditModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-4xl max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.form.editTitle') || 'Modifica Richiesta'}
                                    </h2>
                                    <button
                                        onClick={() => setShowEditModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <ShipperQuoteForm
                                    quoteRequest={quoteRequest}
                                    onSubmit={handleUpdate}
                                    onCancel={() => setShowEditModal(false)}
                                    shipperId={shipperId}
                                />
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Confirm Accept Modal */}
            {confirmAcceptId && (
                <ModalOverlay isOpen={!!confirmAcceptId} onOpenChange={() => setConfirmAcceptId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.responses.confirmAcceptTitle')}
                                    </h2>
                                    <button
                                        onClick={() => setConfirmAcceptId(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common:close') || 'Close'}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-tertiary mb-6">
                                    {t('shipper.responses.confirmAcceptMessage')}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => setConfirmAcceptId(null)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('common:cancel') || 'Annulla'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (id && quoteRequest && confirmAcceptId) {
                                                const updated = shipperQuoteService.updateQuoteResponseStatus(id, confirmAcceptId, 'accepted');
                                                if (updated) {
                                                    setQuoteRequest(updated);
                                                }
                                            }
                                            setConfirmAcceptId(null);
                                        }}
                                        color="primary"
                                        size="md"
                                    >
                                        {t('shipper.responses.confirmAcceptConfirm')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Creating Container Loading Modal */}
            {isCreatingContainer && (
                <ModalOverlay isOpen={isCreatingContainer} onOpenChange={() => { }}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary">
                                <div className="flex flex-col items-center gap-4">
                                    <LoadingIndicator type="line-spinner" size="lg" />
                                    <p className="text-lg font-semibold text-primary">
                                        {t('shipper.detail.creatingContainer') || 'Creazione container in corso...'}
                                    </p>
                                    <p className="text-sm text-tertiary text-center">
                                        {t('shipper.detail.creatingContainerMessage') || 'Stiamo creando il container sulla base delle quote accettate. Attendi...'}
                                    </p>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Complete Request Modal */}
            {isCompleteRequestModalOpen && !isCreatingContainer && (
                <ModalOverlay isOpen={isCompleteRequestModalOpen} onOpenChange={setIsCompleteRequestModalOpen}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.detail.completeRequestTitle') || 'Completa richiesta di preventivo'}
                                    </h2>
                                    <button
                                        onClick={() => setIsCompleteRequestModalOpen(false)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common:close') || 'Close'}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-tertiary mb-6">
                                    {t('shipper.detail.completeRequestMessage') || 'La richiesta verrà segnata come completata. Il sistema simulerà la creazione di uno o più container sulla base delle quote accettate con booking number.'}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => setIsCompleteRequestModalOpen(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('common:cancel') || 'Annulla'}
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            if (id && canCompleteRequest) {
                                                setIsCompleteRequestModalOpen(false);
                                                setIsCreatingContainer(true);
                                                
                                                // Simula creazione container con delay (5-15 secondi)
                                                const delay = 5000 + Math.random() * 10000;
                                                await new Promise(resolve => setTimeout(resolve, delay));
                                                
                                                const result = shipperQuoteService.completeRequestAndSimulateContainers(id);
                                                if (result.request) {
                                                    setQuoteRequest(result.request);
                                                }
                                                
                                                setIsCreatingContainer(false);
                                                
                                                // Redirect alla pagina container se creato
                                                if (result.containerId) {
                                                    navigate(`/shipper/containers/${result.containerId}`);
                                                } else {
                                                    // Se non è stato creato il container, ricarica la pagina
                                                    const updated = shipperQuoteService.getById(id);
                                                    if (updated) {
                                                        setQuoteRequest(updated);
                                                    }
                                                }
                                            }
                                        }}
                                        color="primary"
                                        size="md"
                                        isDisabled={isCreatingContainer || !canCompleteRequest}
                                    >
                                        {isCreatingContainer
                                            ? (t('shipper.detail.creatingContainer') || 'Creazione container...')
                                            : (t('shipper.detail.completeRequestConfirm') || 'Conferma completamento')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Confirm Cancel Modal */}
            {confirmCancelId && (
                <ModalOverlay isOpen={!!confirmCancelId} onOpenChange={() => setConfirmCancelId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.detail.confirmCancel.title') || t('shipper.detail.confirmCancel') || 'Conferma Annullamento'}
                                    </h2>
                                    <button
                                        onClick={() => setConfirmCancelId(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common:close') || 'Close'}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary mb-6">
                                    {t('shipper.detail.confirmCancel.message') || t('shipper.detail.confirmCancelMessage') || 'Sei sicuro di voler annullare questa richiesta di preventivo?'}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        onClick={() => setConfirmCancelId(null)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('common:cancel') || 'Annulla'}
                                    </Button>
                                    <Button
                                        onClick={handleCancel}
                                        color="primary-destructive"
                                        size="md"
                                    >
                                        {t('shipper.detail.confirmCancel.confirm') || t('shipper.detail.confirm') || 'Conferma'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Reminder Confirmation Modal */}
            {reminderRecipientId && (
                <ModalOverlay isOpen={!!reminderRecipientId} onOpenChange={(open) => !open && setReminderRecipientId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {t('shipper.detail.recipients.sendReminderTitle') || 'Invia sollecito'}
                                    </h2>
                                    <button
                                        onClick={() => setReminderRecipientId(null)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common:close') || 'Close'}
                                        disabled={isSendingReminder}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                {(() => {
                                    const recipient = addressBookService.getById(reminderRecipientId);
                                    return (
                                        <>
                                            <p className="text-sm text-tertiary mb-6">
                                                {t('shipper.detail.recipients.sendReminderMessage', {
                                                    name: recipient ? `${recipient.firstName} ${recipient.lastName}` : 'Freight Forwarder',
                                                    email: recipient?.email || ''
                                                }) || `Vuoi inviare un sollecito a ${recipient ? `${recipient.firstName} ${recipient.lastName}` : 'Freight Forwarder'} (${recipient?.email || ''})?`}
                                            </p>
                                            <div className="flex justify-end gap-3">
                                                <Button
                                                    onClick={() => setReminderRecipientId(null)}
                                                    color="secondary"
                                                    size="md"
                                                    isDisabled={isSendingReminder}
                                                >
                                                    {t('common:cancel') || 'Annulla'}
                                                </Button>
                                                <Button
                                                    onClick={() => handleSendReminder(reminderRecipientId)}
                                                    color="primary"
                                                    size="md"
                                                    iconLeading={isSendingReminder ? undefined : Send01}
                                                    isLoading={isSendingReminder}
                                                    isDisabled={isSendingReminder}
                                                >
                                                    {isSendingReminder
                                                        ? (t('shipper.detail.recipients.sendingReminder') || 'Invio in corso...')
                                                        : (t('shipper.detail.recipients.confirmSendReminder') || 'Invia sollecito')}
                                                </Button>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

